import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { useAtom } from 'jotai';
import { omit, uniq } from 'lodash';
import { useMemo } from 'react';

import type {
  FeaturePermissionActionKey,
  FeaturePermissionActionValue,
  PermissionTableRowItems,
  Role,
  RolePermissionsMatrixProps,
  ScopeFeatureKey
} from '@/components/RolePermissions/RolePermissions.types';
import { PRODUCTS } from '@/constants/products.constants';
import { useProductsCheck } from '@/hooks/useProductsCheck';
import type { UserRole } from '@/pages/Administration/Users';
import { userAtom } from '@/store/user.store';

import {
  INTERNAL_FEATURES,
  PERMISSION_ACTIONS_MAP,
  SCOPE_FEATURES
} from './RolePermissions.constants';

const TABLE_ACTIONS: FeaturePermissionActionValue[] = [
  'read',
  'write',
  'delete'
];

const RolePermissionsMatrix = ({
  userRoles,
  allRoles
}: RolePermissionsMatrixProps) => {
  const [user] = useAtom(userAtom);
  const hasVMPEProduct = useProductsCheck([PRODUCTS.VMPE]);
  const hasVMPProduct = useProductsCheck([PRODUCTS.VMP]);
  const hasEDRProduct = useProductsCheck([PRODUCTS.EDR]);

  const scopeFeatures = useMemo(() => {
    if (user?.internal) {
      return SCOPE_FEATURES;
    }
    let features = omit(SCOPE_FEATURES, INTERNAL_FEATURES);

    if (!hasVMPProduct && !hasVMPEProduct) {
      features = omit(features, ['vmp']);
    }

    if (!hasEDRProduct) {
      features = omit(features, ['edr']);
    }

    return features;
  }, [user]);

  const permissions = useMemo(() => {
    return [
      ...new Set(
        userRoles.flatMap((role: UserRole) => {
          const matchedRole = allRoles.find((r: Role) => r.name === role.name);
          return (
            matchedRole?.permissions.filter((permission: string) => {
              const feature = permission.split('.')[0] as ScopeFeatureKey;
              return Object.keys(scopeFeatures).includes(feature);
            }) || []
          );
        })
      )
    ];
  }, [userRoles, allRoles]);

  const userFeaturePermissions = useMemo(() => {
    const tempPermissions: PermissionTableRowItems = {};

    // Initialize all scope features with empty arrays to always display them, even if user doesn't have that permission
    Object.keys(scopeFeatures).forEach((feature) => {
      tempPermissions[feature as ScopeFeatureKey] = [];
    });

    // Populate the permissions for each feature and map the actions to the ones displayed in the UI
    (permissions as string[]).forEach((permission: string) => {
      const [feature, action] = permission.split('.') as [
        ScopeFeatureKey,
        FeaturePermissionActionKey
      ];

      if (PERMISSION_ACTIONS_MAP[action] && tempPermissions[feature]) {
        tempPermissions[feature].push(PERMISSION_ACTIONS_MAP[action]);
      }
      tempPermissions[feature] = uniq(tempPermissions[feature]);
    });

    return tempPermissions;
  }, [permissions]);

  const renderRoleTableCell = (
    featurePermissions: FeaturePermissionActionValue[] | undefined,
    action: FeaturePermissionActionValue
  ) => {
    return (
      <TableCell key={`${action}`} align="center">
        {featurePermissions?.includes(action) && (
          <FontAwesomeIcon icon={faCheck} style={{ color: 'primary.main' }} />
        )}
      </TableCell>
    );
  };

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell />
            {TABLE_ACTIONS.map((action) => (
              <TableCell
                key={action}
                align="center"
                style={{
                  textTransform: 'uppercase'
                }}
              >
                {action}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(userFeaturePermissions).map((feature) => (
            <TableRow key={feature} hover>
              <TableCell sx={{ position: 'sticky', fontWeight: 500 }}>
                {scopeFeatures[feature as ScopeFeatureKey]}
              </TableCell>

              {TABLE_ACTIONS.map((action) => {
                return renderRoleTableCell(
                  userFeaturePermissions[feature as ScopeFeatureKey],
                  action
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default RolePermissionsMatrix;
